<template>
  <div class="country-selection-list-fi">
    <grid-container-fi v-show="continents?.length">
      <div class="country-selection-list-fi__search-container">
        <div
          :class="{
            'country-selection-list-fi__search': true,
            'country-selection-list-fi__search--mobile-search-activated':
              mobileSearchActivated && !isSM,
          }"
        >
          <span class="country-selection-list-fi__search-title">
            {{ $dict.get('global-Labels.Country-Selection.Search-Title') }}
          </span>
          <span class="country-selection-list-fi__search-input-wrapper">
            <text-input-fi
              ref="country-search"
              v-model.trim="search"
              name="country-search"
              class="country-selection-list-fi__search-input"
              :label="$dict.get('global-Labels.Country-Selection.Search-Input-Label')"
              :icon="mobileSearchActivated && !isSM ? 'close' : 'search'"
              :clear="true"
              type="search"
              large-from="sm"
              data-test="country-search-input"
              @icon-clicked="mobileSearchActivated = false"
              @update:model-value="filterCountries"
            />
            <icon-fi
              v-if="!mobileSearchActivated"
              icon="search"
              class="country-selection-list-fi__search-icon-mobile"
              @click="mobileSearchActivated = !mobileSearchActivated"
            />
          </span>
        </div>

        <div
          v-if="$slots.info"
          class="country-selection-list-fi__info"
        >
          <slot name="info" />
        </div>

        <div v-show="showNoCountryFoundHint">
          <div class="country-selection-list-fi__no-result-icon">
            <icon-fi
              icon="search"
              size="2"
            />
          </div>
          <div class="country-selection-list-fi__no-result-headline">
            {{ $dict.get('global-Labels.Country-Selection.Search-No-Results-Headline') }}
          </div>
          <!-- eslint-disable vue/no-v-html -->
          <div
            class="country-selection-list-fi__no-result"
            v-html="
              $dict.get('global-Labels.Country-Selection.Search-No-Results', [
                `<span id='search-term'></span>`,
              ]) || `<span class='fi-hidden' id='search-term'></span>`
            "
          />
          <!-- eslint-enable vue/no-v-html -->
          <teleport
            v-if="isMounted && showNoCountryFoundHint"
            to="#search-term"
            >{{ search }}</teleport
          >
        </div>
      </div>
    </grid-container-fi>
    <grid-container-fi v-if="continents?.length && !showNoCountryFoundHint">
      <div class="country-selection-list-fi__container">
        <ul class="country-selection-list-fi__list">
          <template
            v-for="(continent, index) in continents"
            :key="continent.displayName"
          >
            <li
              v-if="filteredCountries(continent.countries)?.length > 0"
              class="country-selection-list-fi__continent-container"
              :class="index < continents.length - 1 ? 'lg:fi-col-span-12' : 'lg:fi-col-span-6'"
            >
              <div class="country-selection-list-fi__continent-header">
                {{ continent.displayName }}
              </div>

              <grid-container-fi :container="false">
                <template
                  v-for="country in filteredCountries(continent.countries)"
                  :key="country.displayName"
                >
                  <div class="country-selection-list-fi__continent-wrapper">
                    <country-flag-fi
                      v-if="country?.value && country.value !== 'en-ZZ'"
                      :country="country.value.toLowerCase()"
                      class="country-selection-list-fi__icon"
                    />
                    <icon-fi
                      v-else
                      size="0_8"
                      icon="globe"
                      class="country-selection-list-fi__icon country-selection-list-fi__icon--svg"
                    />
                    <template v-if="country.value !== 'en-ZZ'">
                      <span
                        v-if="country?.title"
                        class="country-selection-list-fi__list-title"
                      >
                        {{ $fischer.brandName() }} {{ country.title }}
                      </span>
                      <br class="country-selection-list-fi__break" />
                    </template>
                    <span
                      v-if="
                        country?.localizations?.length &&
                        (countryUrlKey ? country[countryUrlKey]?.url : country.link?.url)
                      "
                      :class="{
                        'country-selection-list-fi__languages': true,
                        'country-selection-list-fi__languages--international':
                          country.value === 'en-ZZ',
                      }"
                    >
                      <component
                        :is="redirectLayer ? 'span' : 'a'"
                        v-for="language in country.localizations"
                        :key="language.title"
                        :href="
                          redirectLayer || onlyEmitOnClick ? null : countryUrl(country, language)
                        "
                        :class="{
                          'country-selection-list-fi__url': true,
                          'country-selection-list-fi__url--redirect': redirectLayer,
                        }"
                        @click="onCountryClick(country, language)"
                      >
                        {{ language.name }}
                      </component>
                    </span>
                  </div>
                </template>
              </grid-container-fi>
            </li>
          </template>
        </ul>
      </div>
    </grid-container-fi>

    <div
      v-if="redirectLayer"
      :class="{
        'country-selection-list-fi__redirect-layer': true,
        'country-selection-list-fi__redirect-layer--active': showRedirectLayer,
      }"
    >
      <grid-container-fi>
        <div class="country-selection-list-fi__redirect-content">
          <div class="country-selection-list-fi__redirect-text-content">
            <div class="country-selection-list-fi__redirect-headline">
              {{ fields.redirectLayerHeadline?.value }}
            </div>
            <!-- eslint-disable vue/no-v-html -->
            <div
              class="country-selection-list-fi__redirect-description"
              v-html="
                getPreparedPlaceholderValue(
                  fields,
                  'redirectLayerDescription',
                  activeCountry?.title,
                )
              "
            />
            <!-- eslint-enable vue/no-v-html -->
          </div>
          <div class="country-selection-list-fi__redirect-controls">
            <div class="country-selection-list-fi__redirect-disagree">
              <button-fi
                :class="{
                  'button-fi__tertiary button-fi__xs-streched': true,
                  'button-fi--large-from-sm': true,
                }"
                type="button"
                @click="closeRedirectLayer"
              >
                {{ fields.redirectLayerBtnDisagree?.value }}
              </button-fi>
            </div>
            <div class="country-selection-list-fi__redirect-agree">
              <button-fi
                :class="{
                  'button-fi__primary button-fi__xs-streched': true,
                  'button-fi--large-from-sm': true,
                }"
                type="button"
                @click="redirectToCountry"
              >
                {{ fields.redirectLayerBtnAgree?.value }}
              </button-fi>
            </div>
          </div>
        </div>
      </grid-container-fi>
    </div>
  </div>
</template>

<script setup lang="ts">
import { captureException } from '@sentry/browser';
import { ButtonFi, CountryFlagFi, IconFi, TextInputFi } from 'atoms';
import { computed, inject, onMounted, onServerPrefetch, ref, useSSRContext } from 'vue';

import GridContainerFi from '@/components/grid-fi/GridContainerFi.vue';
import { useBreakpoints } from '@/composables/breakpoint';
import type { LocaleCountry } from '@/composables/countryList';
import { useFischer } from '@/composables/fischerPlugin';
import { getPreparedPlaceholderValue } from '@/utils/fieldTransformations';

defineOptions({ name: 'CountrySelectionListFi' });

interface Props {
  countriesFilter?: string | null;
  countryUrlKey?: string | null;
  redirectLayer?: boolean;
  onlyEmitOnClick?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  countriesFilter: null,
  countryUrlKey: null,
  redirectLayer: false,
  onlyEmitOnClick: false,
});

const emit = defineEmits<{
  'country-clicked': [country: LocaleCountry, language: LocaleCountry];
}>();

const fields = inject<Record<string, any>>('fields', {});

const { isSM } = useBreakpoints();
const fischer = useFischer();

/**
 * The server fetches the country list during onServerPrefetch and renders it, so the client's first
 * render - the hydration render, before its own fetch can resolve - has to reproduce that from the
 * markup. It is handed over through __PREFETCH__, keyed because a rendering's payload is shared.
 */
const PREFETCH_KEY = 'countrySelection';

const prefetch = inject<Record<string, any> | undefined>('prefetch', undefined);
const ssrContext: Record<string, any> = (IS_SSR && useSSRContext()) || {};

const countryData = ref<any>((!IS_SSR && prefetch?.[PREFETCH_KEY]) || null);
const filteredCountryData = ref<any>(null);
const search = ref('');
const mobileSearchActivated = ref(false);
const showRedirectLayer = ref(false);
const activeCountry = ref<any>(null);
const isMounted = ref(false);

const countryList = computed(() => filteredCountryData.value || countryData.value);
const continents = computed(
  () => countryList.value?.continents?.continents as undefined | Array<any>,
);

const showNoCountryFoundHint = computed(() =>
  filteredCountryData.value?.continents.continents.every(
    (continent: any) => !continent.countries || continent.countries?.length === 0,
  ),
);

const getApi = () => {
  if (IS_SHOWROOM) {
    return '/__mocks__/sitecoreApi/api/i18n/countries.json';
  }
  const path = '/api/i18n/countries';
  if (IS_SSR) {
    return `${fischer.hostUrlBase()}${path}`;
  }
  return path;
};

const fetchCountries = async () => {
  if (countryData.value) {
    return;
  }
  try {
    const api = getApi();
    let url = api;
    if (!IS_SHOWROOM) {
      const params = new URLSearchParams({ language: fischer.siteLanguage() });
      url = `${api}?${params.toString()}`;
    }
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${url}: ${response.status}`);
    }
    const data = await response.json();

    countryData.value = data;
    if (IS_SSR) {
      ssrContext.prefetch = { ...ssrContext.prefetch, [PREFETCH_KEY]: data };
    }
  } catch (error) {
    console.error(error);
    captureException(error);
  }
};

// created / serverPrefetch equivalent. The hook takes a function, not the promise itself - handing
// it the promise made Vue call a non-function and abort the render with `hook is not a function`.
const fetchCountriesPromise = fetchCountries();
onServerPrefetch(() => fetchCountriesPromise);

onMounted(() => {
  isMounted.value = true;
});

const countryUrl = (country: any, language: any) => {
  if (props.countryUrlKey && country[props.countryUrlKey]?.url) {
    return country[props.countryUrlKey]?.url;
  }

  return `${country.link?.url}/${language.iso_Code.toLowerCase()}`;
};

const filteredCountries = (countries: any[]) => {
  if (props.countriesFilter) {
    return countries?.filter((c) => props.countriesFilter && c[props.countriesFilter]?.url);
  }
  return countries?.slice().sort((a, b) => a.title.localeCompare(b.title));
};

const filterCountries = () => {
  filteredCountryData.value = JSON.parse(JSON.stringify(countryData.value));
  filteredCountryData.value.continents.continents.forEach((continent: any) => {
    continent.countries = continent.countries?.filter(
      (c: any) =>
        c.displayName.toLowerCase().includes(search.value.toLowerCase()) ||
        c.title.toLowerCase().includes(search.value.toLowerCase()) ||
        c.localizations.some((l: any) => l.name.toLowerCase().includes(search.value.toLowerCase())),
    );
  });
};

const onCountryClick = (country: LocaleCountry, language: LocaleCountry) => {
  activeCountry.value = country;
  showRedirectLayer.value = true;
  emit('country-clicked', country, language);
};

const closeRedirectLayer = () => {
  showRedirectLayer.value = false;
};

const redirectToCountry = () => {
  let redirectUrl = activeCountry.value.link?.url;
  if (props.countryUrlKey && activeCountry.value[props.countryUrlKey]?.url) {
    redirectUrl = activeCountry.value[props.countryUrlKey].url;
  }
  window.location.href = redirectUrl;
};
</script>

<style scoped src="./country-selection-list-fi" />
